// src/Dashboard/pages/user/Messaging/components/MessageReadDrawer.jsx

import React, { useEffect, useMemo } from "react";
import { FaReply, FaTimes, FaTrash } from "react-icons/fa";

import {
  formatMessageDate,
  formatMessageTime,
  getMessageDateValue,
  getUserAvatar,
  getUserFullName,
  sanitizeLight,
} from "../utils/messagingUtils";

export default function MessageReadDrawer({
  isOpen,
  type = "received",
  message,
  onClose,
  onReply,
  onDelete,
}) {
  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, onClose]);

  const contact = type === "received" ? message?.Sender : message?.Recipient;
  const safeContent = useMemo(
    () => sanitizeLight(message?.content || ""),
    [message?.content]
  );

  if (!isOpen || !message) return null;

  return (
    <div className="fixed inset-0 z-[120]">
      <div
        className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute inset-y-0 right-0 flex w-full max-w-4xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <header className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-4 dark:border-slate-800 sm:px-6">
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
              {type === "received" ? "Message reçu" : "Message envoyé"}
            </p>

            <h2 className="truncate text-lg font-bold text-slate-900 dark:text-white sm:text-xl">
              {message.subject || "Sans objet"}
            </h2>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
          >
            <FaTimes />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
            <div className="flex items-center gap-4">
              <img
                src={getUserAvatar(contact)}
                alt={getUserFullName(contact)}
                className="h-14 w-14 rounded-2xl object-cover ring-1 ring-slate-200 dark:ring-slate-700"
              />

              <div className="min-w-0">
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {type === "received" ? "De" : "À"}
                </p>

                <p className="truncate text-base font-bold text-slate-900 dark:text-white">
                  {getUserFullName(contact)}
                </p>

                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  {formatMessageDate(getMessageDateValue(message))} à{" "}
                  {formatMessageTime(getMessageDateValue(message))}
                </p>
              </div>
            </div>
          </div>

          <article className="mt-5 rounded-[24px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-950">
            <div
              className="prose prose-sm max-w-none text-slate-700 dark:prose-invert dark:text-slate-200"
              dangerouslySetInnerHTML={{
                __html:
                  safeContent ||
                  '<p class="italic text-slate-500">Message vide.</p>',
              }}
            />
          </article>
        </div>

        <footer className="border-t border-slate-200 bg-white/95 p-4 dark:border-slate-800 dark:bg-slate-900/95">
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={() => onDelete?.(message.id)}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-red-50 px-4 py-3 text-sm font-bold text-red-700 ring-1 ring-red-200 transition hover:bg-red-100 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-500/20"
            >
              <FaTrash />
              Supprimer
            </button>

            {type === "received" && (
              <button
                type="button"
                onClick={() => onReply?.(message)}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-indigo-800"
              >
                <FaReply />
                Répondre
              </button>
            )}
          </div>
        </footer>
      </aside>
    </div>
  );
}